/* Lead origin: which campaign a B2B application came from.

   The application form (snippets/b2b-application-form.liquid) is Shopify's
   native contact form, which emails the shop inbox. This script fills two
   hidden fields of that form so the email says where the visit came from:
   - [data-lead-origin="summary"]: "source / medium / campaign / content"
     from the UTM parameters, or "directo" when there are none. " (fbclid)"
     is appended when the visit carried a Meta click id (its presence only,
     never the id).
   - [data-lead-origin="landing"]: the path of the first page of the visit.

   First touch. A visitor may land with UTMs on one page and reach the form
   later, so layout/theme.liquid loads this file on every page and the first
   UTMs seen are kept in sessionStorage for the rest of the tab session. A
   later page without UTMs never clears them, and later UTMs never replace
   the first ones.

   Nothing here is allowed to get in the way of the form. Liquid renders the
   fields with a default ("sin dato"), so the form posts the same without
   JavaScript. Every storage access is guarded: with storage blocked the
   fields are filled from the current URL alone. No error reaches the
   visitor, nothing is requested, and nothing personal is read: UTM values
   and a path, each stripped of control characters and capped in length, so
   a crafted URL cannot put a long or multi-line payload in the email.

   A plain script with no build step: it attaches `LeadOrigin` to the global
   object and runs once when there is a document. ci/checks/lead-origin.test.mjs
   runs this same file in a Node VM. */

(function (root) {
  'use strict';

  var STORAGE_KEY = 'jaleo-lead-origin';
  var MAX_LENGTH = 100;
  var UTM_FIELDS = ['source', 'medium', 'campaign', 'content'];
  var DIRECT = 'directo';
  var MISSING_PART = '-';
  var FBCLID_HINT = ' (fbclid)';

  /* One line, no control characters, at most MAX_LENGTH characters. */
  function clean(value) {
    if (typeof value !== 'string') return '';
    return value
      .replace(/[\u0000-\u001F\u007F-\u009F\u2028\u2029]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX_LENGTH)
      .trim();
  }

  function emptyOrigin() {
    return { source: '', medium: '', campaign: '', content: '', fbclid: false, landing: '' };
  }

  /* Coerces anything (a parsed storage value included) into a clean origin. */
  function normalize(raw) {
    var origin = emptyOrigin();
    if (!raw || typeof raw !== 'object') return origin;
    UTM_FIELDS.forEach(function (field) {
      origin[field] = clean(raw[field]);
    });
    origin.fbclid = raw.fbclid === true;
    origin.landing = clean(raw.landing);
    return origin;
  }

  function hasUtm(origin) {
    return UTM_FIELDS.some(function (field) {
      return origin[field] !== '';
    });
  }

  /* The origin the current URL describes. The query string is only read for
     the UTM parameters and for whether fbclid is there. */
  function fromUrl(search, pathname) {
    var origin = emptyOrigin();
    origin.landing = clean(pathname);
    var params;
    try {
      params = new URLSearchParams(typeof search === 'string' ? search : '');
    } catch (error) {
      return origin;
    }
    UTM_FIELDS.forEach(function (field) {
      origin[field] = clean(params.get('utm_' + field) || '');
    });
    origin.fbclid = clean(params.get('fbclid') || '') !== '';
    return origin;
  }

  /* First touch wins. The UTMs travel as a group: the stored ones are kept
     whole, and the current ones only come in when none were stored. */
  function merge(stored, current) {
    var merged = normalize(stored);
    var now = normalize(current);
    if (!hasUtm(merged) && hasUtm(now)) {
      UTM_FIELDS.forEach(function (field) {
        merged[field] = now[field];
      });
    }
    merged.fbclid = merged.fbclid || now.fbclid;
    if (merged.landing === '') merged.landing = now.landing;
    return merged;
  }

  function summary(origin) {
    var clean_origin = normalize(origin);
    var text = DIRECT;
    if (hasUtm(clean_origin)) {
      text = UTM_FIELDS.map(function (field) {
        return clean_origin[field] || MISSING_PART;
      }).join(' / ');
    }
    return clean_origin.fbclid ? text + FBCLID_HINT : text;
  }

  /* Blocked storage throws on the property itself, not only on its methods. */
  function sessionStorageOf(win) {
    try {
      return win.sessionStorage || null;
    } catch (error) {
      return null;
    }
  }

  function load(storage) {
    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? normalize(parsed) : null;
    } catch (error) {
      return null;
    }
  }

  function save(storage, origin) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(normalize(origin)));
      return true;
    } catch (error) {
      return false;
    }
  }

  function fill(doc, origin) {
    var values = { summary: summary(origin), landing: normalize(origin).landing };
    var fields = doc.querySelectorAll('[data-lead-origin]');
    for (var index = 0; index < fields.length; index += 1) {
      var value = values[fields[index].getAttribute('data-lead-origin')];
      /* An empty value keeps the default Liquid rendered. */
      if (value) fields[index].value = value;
    }
  }

  /* Remembers the origin of this visit and writes it in the form fields of
     the page, if any. Never throws. */
  function run(win) {
    try {
      var storage = sessionStorageOf(win);
      var current = fromUrl(win.location.search, win.location.pathname);
      var origin = merge(storage ? load(storage) : null, current);
      if (storage) save(storage, origin);
      if (win.document) fill(win.document, origin);
      return origin;
    } catch (error) {
      return null;
    }
  }

  root.LeadOrigin = {
    STORAGE_KEY: STORAGE_KEY,
    MAX_LENGTH: MAX_LENGTH,
    clean: clean,
    fromUrl: fromUrl,
    merge: merge,
    summary: summary,
    run: run,
  };

  if (root.document && root.location) run(root);
})(typeof window !== 'undefined' ? window : globalThis);
